import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || ''
});

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/api/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many reset requests. Please try again later.');
      } else {
        setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold mb-4 text-primary">Check Your Email</h1>
          
          <p className="text-muted-foreground mb-6">
            If an account exists with <span className="text-cyan-400">{email}</span>, we've sent a password reset link.
          </p>
          
          <p className="text-sm text-muted-foreground mb-6">
            The link will expire in <span className="text-yellow-400 font-semibold">15 minutes</span>.
          </p>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setSubmitted(false);
                setEmail('');
              }}
              className="w-full py-3 bg-white/5 hover:bg-white/10 border border-cyan-400/20 text-foreground rounded-lg transition-all"
            >
              Send Another Email
            </button>
            
            <Link
              to="/login"
              className="block w-full py-3 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg text-center transition-all"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="glass-card rounded-xl p-8 w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img 
            src="/logo.png" 
            alt="Rook Games" 
            className="h-20 w-20 object-contain mb-4"
            data-testid="forgot-password-logo"
          />
          <h1 className="text-4xl font-bold text-center text-primary">KRAKEN</h1>
          <p className="text-center text-muted-foreground mt-2">Reset your password</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-cyan-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all"
              placeholder="Enter your email"
              required
              data-testid="forgot-email-input"
            />
          </div>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm" data-testid="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-cyan-500/90 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-lg btn-glow transition-all"
            data-testid="submit-button"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300 transition-colors">
            ← Back to Login
          </Link>
        </div>

        <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10">
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Security Note:</strong> Password reset links expire after 15 minutes. If you don't receive an email, check your spam folder.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
